import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ESS_LOGO, NAV_LINKS } from "@/lib/brand";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="ess-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-ess-blush"
          : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="ess-container flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group shrink-0"
        >
          <img
            src={ESS_LOGO}
            alt="The Exit Strategy Society"
            className="h-12 w-12 object-contain"
          />
        </Link>

        <nav className="hidden xl:flex flex-1 items-center justify-end gap-5">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={link.testid}
              className={({ isActive }) =>
                `ess-link text-[12px] uppercase tracking-[0.22em] transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-ess-emerald font-semibold"
                    : "text-ess-charcoal/80 hover:text-ess-emerald"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <Link
            to="/book"
            data-testid="nav-book-consultation"
            className="ml-2 inline-flex items-center bg-ess-emerald text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-ess-emerald-dark transition-all hover:-translate-y-[1px] whitespace-nowrap"
          >
            Book a Consultation
          </Link>
        </nav>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="xl:hidden text-ess-emerald p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="xl:hidden bg-white border-t border-ess-blush"
        >
          <div className="ess-container py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`m-${link.testid}`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] ${
                    isActive ? "text-ess-emerald" : "text-ess-charcoal/80"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="m-nav-book-consultation"
              className="mt-2 inline-flex w-fit items-center bg-ess-emerald text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em]"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
